import { useCallback } from 'react';
import { useSocketEvent } from '@connect-x-app/hybrid-data-access';
import {
  AllMessagesReadSocket,
  MessageDeliveredSocket,
  MessageReadSocket,
  MessageStatus,
  ReceiveSocketMessage,
} from '@connect-x-app/shared-domain-models';
import { mapSocketMessageToMessage } from '@connect-x-app/shared-utils';

import { useAuthenticationStore, useMessageSessionStore, useMessageStore, useSocketStore } from '../../stores';
import { useChatMessageNotifications } from '../useChatMessageNotifications';
import { useChatMessageSocketSendEvents } from './useChatMessageSocketSendEvents';

export const useChatMessageSocketEvents = () => {
  const { socket } = useSocketStore();
  const { user } = useAuthenticationStore();
  const { addMessage, markMessagesReadByIds, updateMessageStatus } = useMessageStore();
  const { hasLoadedLastPage } = useMessageSessionStore();
  const { sendChatDeliverMessage } = useChatMessageSocketSendEvents();
  const { incrementChannelMessageCount, incrementFlightRoomMessageCount } = useChatMessageNotifications();

  const handleChatMessageSent = useCallback(
    (sentMessage: ReceiveSocketMessage) => {
      const isSender = sentMessage.senderId === user?.id || sentMessage.senderId === user?.externalId;

      const message = mapSocketMessageToMessage(sentMessage);
      addMessage({ addToMostRecentChunk: hasLoadedLastPage, message: message });

      if (!isSender) {
        sendChatDeliverMessage({
          channelId: message.channelId,
          chatroomId: message.room,
          messageId: message.id,
        });

        incrementFlightRoomMessageCount(message.room);
        incrementChannelMessageCount(message.room, message.channelId, message.id);
      }
    },
    [
      user?.id,
      user?.externalId,
      addMessage,
      hasLoadedLastPage,
      sendChatDeliverMessage,
      incrementFlightRoomMessageCount,
      incrementChannelMessageCount,
    ],
  );

  const handleChatMessageDelivered = useCallback(
    (deliveredMessage: MessageDeliveredSocket) => {
      if (deliveredMessage.deliveredTo.externalId === user?.externalId) {
        return;
      }
      updateMessageStatus({
        channelId: deliveredMessage.channelId,
        messageId: deliveredMessage.messageId,
        status: MessageStatus.DELIVERED,
        userExternalId: deliveredMessage.deliveredTo.externalId,
      });
    },
    [updateMessageStatus, user?.externalId],
  );

  const handleChatMessageRead = useCallback(
    (readMessage: MessageReadSocket) => {
      if (readMessage.readBy.externalId === user?.externalId) {
        return;
      }
      updateMessageStatus({
        channelId: readMessage.channelId,
        messageId: readMessage.messageId,
        status: MessageStatus.READ,
        userExternalId: readMessage.readBy.externalId,
      });
    },
    [updateMessageStatus, user?.externalId],
  );

  const handleChatAllMessagesRead = useCallback(
    (event: AllMessagesReadSocket) => {
      markMessagesReadByIds({
        channelId: event.channelId,
        messageIds: event.messageIds,
        readBy: event.readBy,
      });
    },
    [markMessagesReadByIds],
  );

  useSocketEvent(socket, 'chat.message-sent', handleChatMessageSent);
  useSocketEvent(socket, 'chat.message-delivered', handleChatMessageDelivered);
  useSocketEvent(socket, 'chat.message-read', handleChatMessageRead);
  useSocketEvent(socket, 'chat.all-messages-read', handleChatAllMessagesRead);
};
